/**
 * 吸附候选评分 —— 对应技术设计 §5.3「空间接近 + 语义兼容 + 当前任务」。
 *
 * 【§22 红线 1 就落在这个文件里】
 * 「连接必须基于语义端口和关系图，不能只用坐标重叠判断实验是否正确。」
 * 实现上的兑现方式是**淘汰顺序**：
 *
 *     语义兼容淘汰（anchorsCompatible） → 动作文法淘汰（resolveBond） → 才算距离
 *
 * 距离在最后。所以「两件器材画面上完全重叠、但端口语义不兼容」永远不会成为候选，
 * 无论它们离得多近。反过来，「没有像素重叠但在热区半径内且语义兼容」是合法候选 ——
 * 这正是 §14.1「不要求像真实 3D 仿真一样精准对齐，允许接近即吸附」。
 */

import { describeAnchors } from "../eom/equipment-registry.js";
import type { LabObject } from "../eom/lab-object.js";
import { toWorld } from "../eom/lab-object.js";
import type { AnchorFacet } from "../eom/port.js";
import { anchorsCompatible } from "../eom/port.js";
import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { listObjects, listRelations } from "../eom/assembly-graph.js";
import type { Bond } from "./action-grammar.js";
import { resolveBond } from "./action-grammar.js";
import { resolveTubeEnds } from "./constraint-solver.js";
import type { InputProfile } from "./input-profile.js";
import { effectiveSnapRadius } from "./input-profile.js";

/** §5.3 评分权重。距离占主，当前步骤目标次之，端口语义亲和度兜底做同分裁决。 */
const WEIGHT_DISTANCE = 0.6;
const WEIGHT_TASK = 0.3;
const WEIGHT_AFFINITY = 0.1;

export interface SnapCandidate {
  bond: Bond;
  /** 两个锚点的世界坐标距离 */
  distance: number;
  /** 本次判定采用的有效吸附半径（已含档位缩放） */
  radius: number;
  score: number;
  /** 主动侧锚点的世界坐标 —— 吸附对齐时要把它移到 targetWorld */
  sourceWorld: { x: number; y: number };
  targetWorld: { x: number; y: number };
}

export interface SnapContext {
  graph: AssemblyGraph;
  profile: InputProfile;
  /**
   * 当前 Mission 步骤期望建立的关系 id 集合（§5.3「当前任务」维度、§5.6 分段引导）。
   * M1b 的 mission-engine 会填它；未接入时为空集，评分退化为「距离 + 亲和度」。
   */
  desiredRelationKeys?: ReadonlySet<string>;
}

function distanceBetween(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * 锚点的世界坐标。**柔性导管要特殊取**。
 *
 * 导管的形状由两端锚点决定、不跟自身 pose 走（见 svg-layer.syncConduit），
 * 所以它的端口画在解析出的两端上。如果吸附计算仍按 pose 算，画面位置与判定位置
 * 就会分家：学生看到出气端明明贴着瓶口，引擎却认为它还在几百像素外，
 * 于是「怎么都接不上」——`bug/023-化学/002-bug.md`「无法完整跑完闭环」卡在第 5 步的真因。
 *
 * 一处修正，snap 与 diagnose 共用，不在各处特判。
 */
function anchorWorld(
  graph: AssemblyGraph,
  object: LabObject,
  facet: AnchorFacet,
): { x: number; y: number } {
  const isConduit = (object.ports ?? []).some((port) => port.kind === "tube_path");
  if (isConduit && facet.anchorType === "port" && facet.anchorId !== null) {
    const ends = resolveTubeEnds(graph, object.id);
    if (ends) {
      if (facet.anchorId === "input") return ends.input;
      if (facet.anchorId === "output") return ends.output;
      if (facet.anchorId === "path") {
        return { x: (ends.input.x + ends.output.x) / 2, y: (ends.input.y + ends.output.y) / 2 };
      }
    }
  }
  return toWorld(object, facet.anchor);
}

/** 端口对端口的亲和度高于「整件器材落进槽位」这种粗放动作，用于同分裁决。 */
function affinityOf(bond: Bond): number {
  if (bond.from.anchorType === "port" && bond.to.anchorType === "port") return 1;
  if (bond.from.anchorType === "object" || bond.to.anchorType === "object") return 0.4;
  return 0.7;
}

/** 关系去重键：同一对锚点之间只允许一条 active 关系。 */
export function bondKey(bond: Bond): string {
  const anchor = (facet: AnchorFacet): string =>
    facet.anchorId === null ? facet.objectId : `${facet.objectId}.${facet.anchorId}`;
  return `${bond.relation}:${anchor(bond.from)}->${anchor(bond.to)}`;
}

/**
 * 该锚点是否已经满了。
 *
 * **端口独占，槽位按容量**（缺省不限）：一个试管口只能塞一个塞子，
 * 但试管内部要同时装得下高锰酸钾和棉花。早期把槽位也按独占处理，
 * 结果「加了药就放不进棉花」，学生卡在第 3 步 —— `bug/023-化学/002-bug.md` 第 1、2 条。
 */
function isOccupied(
  graph: AssemblyGraph,
  facet: AnchorFacet,
  object?: LabObject,
  draggedId?: string,
): boolean {
  if (facet.anchorType === "object") return false;

  // **正被拖动的器材，它自己的端口不算被占**：学生把导管从水里拿起来插进瓶口，
  // 这一端在手上，当然可以改插别处。不放行的话，任何接过一次的端口就再也动不了了
  // （`bug/023-化学/002-bug.md`「无法完整跑完闭环」卡在第 5 步的原因）。
  if (draggedId !== undefined && facet.objectId === draggedId) return false;

  const used = listRelations(graph).filter((relation) => {
    if (relation.state !== "active") return false;
    for (const endpoint of [relation.from, relation.to]) {
      if (endpoint.objectId !== facet.objectId) continue;
      if (endpoint.portId === facet.anchorId || endpoint.slotId === facet.anchorId) return true;
    }
    return false;
  }).length;

  if (facet.anchorType === "port") return used >= 1;

  const capacity = object?.slots?.find((slot) => slot.id === facet.anchorId)?.capacity;
  return capacity !== undefined && used >= capacity;
}

/**
 * 为**正在被拖动的器材**找出全部吸附候选，按分数从高到低排序。
 *
 * `dragged` 传的是拖拽中的临时对象（pose 已更新但还没写回图），
 * 所以这里不从 graph 里取它 —— 图里那份 pose 是旧的。
 */
export function findSnapCandidates(dragged: LabObject, context: SnapContext): SnapCandidate[] {
  const { graph, profile } = context;
  const draggedAnchors = describeAnchors(dragged);
  const candidates: SnapCandidate[] = [];

  for (const other of listObjects(graph)) {
    if (other.id === dragged.id) continue;

    for (const otherAnchor of describeAnchors(other)) {
      for (const draggedAnchor of draggedAnchors) {
        // ① 语义兼容 —— 在任何坐标计算之前。红线 1。
        if (!anchorsCompatible(draggedAnchor, otherAnchor)) continue;

        // ② 动作文法：兼容还不够，得能构成一个已知动作，并定出方向
        const bond = resolveBond(draggedAnchor, otherAnchor);
        if (!bond) continue;

        // ③ 占用检查：锚点已被别的 active 关系占了就不再是候选
        const fromObj = bond.from.objectId === dragged.id ? dragged : other;
        const toObj = bond.to.objectId === dragged.id ? dragged : other;
        if (
          isOccupied(graph, bond.from, fromObj, dragged.id) ||
          isOccupied(graph, bond.to, toObj, dragged.id)
        ) {
          continue;
        }

        // ④ 到这一步才算距离
        const sourceWorld = anchorWorld(graph, fromObj, bond.from);
        const targetWorld = anchorWorld(graph, toObj, bond.to);
        const distance = distanceBetween(sourceWorld, targetWorld);

        const radius = effectiveSnapRadius(Math.max(bond.from.radius, bond.to.radius), profile);
        if (distance > radius) continue;

        const proximity = 1 - distance / radius;
        const isDesired = context.desiredRelationKeys?.has(bondKey(bond)) ? 1 : 0;
        const score =
          WEIGHT_DISTANCE * proximity + WEIGHT_TASK * isDesired + WEIGHT_AFFINITY * affinityOf(bond);

        candidates.push({ bond, distance, radius, score, sourceWorld, targetWorld });
      }
    }
  }

  return candidates.sort((a, b) => b.score - a.score);
}

/** 最高分候选，没有则 null。拖拽中每帧调用，用于画预览虚线。 */
export function bestSnapCandidate(dragged: LabObject, context: SnapContext): SnapCandidate | null {
  return findSnapCandidates(dragged, context)[0] ?? null;
}

/* ────────────────────────────────────────────────────────────────────────
 * 失败诊断
 *
 * 来源：`bug/023-化学/001-bug.md` 第 2 条 ——
 * 「错误了，或者没有连上，不知道是代码问题，标注问题，还是什么，总是说连不上」。
 *
 * 原来失败只有一句「没有找到可连接的位置」，学生（和调试的人）分不清是
 * ①这两件器材本来就不该连 ②方向对但离太远 ③目标端口已被占用 ④这件器材根本不能动。
 * 四种情况的下一步动作完全不同，必须分开说。
 *
 * 诊断只在**松手失败时**跑一次，不进每帧路径。
 * ────────────────────────────────────────────────────────────────────── */

export type SnapFailureReason =
  /** 语义上就不该连（火焰接不进集气瓶口） */
  | "incompatible"
  /** 语义兼容、方向也成立，只是离得太远 */
  | "too_far"
  /** 目标锚点已被别的连接占用 */
  | "occupied"
  /** 兼容，但两个锚点凑不成任何已知动作 */
  | "no_action";

export interface SnapFailure {
  reason: SnapFailureReason;
  /** 差一点就成的那一对（incompatible 时为 null） */
  bond: Bond | null;
  /** 最接近的目标器材 id，用于生成文案 */
  targetObjectId: string | null;
  targetAnchorLabel: string | null;
  sourceAnchorLabel: string | null;
  distance: number | null;
  radius: number | null;
}

function anchorLabel(facet: AnchorFacet, object: LabObject | undefined): string {
  if (facet.anchorType === "object") return object?.name ?? facet.objectId;
  const port = object?.ports?.find((p) => p.id === facet.anchorId);
  return port?.label ?? facet.anchorId ?? facet.objectId;
}

/**
 * 松手没吸上时，说明卡在哪一步。
 *
 * 【只诊断学生真正瞄准的那件器材】
 * 第一版是扫全场找「最接近成功的配对」，结果是错的：场上总能找到某个语义兼容的配对
 * （集气瓶口永远和导管出气端兼容），于是把集气瓶丢到酒精灯上，得到的提示却是
 * 「离导管还有 148px」—— 答非所问，`incompatible` 与 `occupied` 两种原因几乎永远轮不到。
 *
 * 学生的心智是「我把 A 丢到 B 上，为什么没连上」。所以先定出 B ——
 * **锚点距离最近的那件器材** —— 再只针对这一对给结论。
 */
export function diagnoseSnapFailure(dragged: LabObject, context: SnapContext): SnapFailure | null {
  const { graph, profile } = context;
  const draggedAnchors = describeAnchors(dragged);
  if (draggedAnchors.length === 0) return null;

  // ① 定出「丢在谁身上」：任意两锚点间的最小距离最小的那件器材
  let target: { object: LabObject; distance: number } | null = null;
  for (const other of listObjects(graph)) {
    if (other.id === dragged.id) continue;
    let nearest = Infinity;
    for (const otherAnchor of describeAnchors(other)) {
      for (const draggedAnchor of draggedAnchors) {
        const distance = distanceBetween(
          anchorWorld(graph, dragged, draggedAnchor),
          anchorWorld(graph, other, otherAnchor),
        );
        if (distance < nearest) nearest = distance;
      }
    }
    if (!target || nearest < target.distance) target = { object: other, distance: nearest };
  }
  if (!target) return null;

  const other = target.object;

  // ② 只在这一对之间找最接近成功的一次配对
  let best: SnapFailure | null = null;
  let sawCompatible = false;

  for (const otherAnchor of describeAnchors(other)) {
    for (const draggedAnchor of draggedAnchors) {
      if (!anchorsCompatible(draggedAnchor, otherAnchor)) continue;
      sawCompatible = true;

      const bond = resolveBond(draggedAnchor, otherAnchor);
      if (!bond) {
        const distance = distanceBetween(
          anchorWorld(graph, dragged, draggedAnchor),
          anchorWorld(graph, other, otherAnchor),
        );
        if (!best || distance < (best.distance ?? Infinity)) {
          best = {
            reason: "no_action",
            bond: null,
            targetObjectId: other.id,
            targetAnchorLabel: anchorLabel(otherAnchor, other),
            sourceAnchorLabel: anchorLabel(draggedAnchor, dragged),
            distance,
            radius: null,
          };
        }
        continue;
      }

      const fromObject = bond.from.objectId === dragged.id ? dragged : other;
      const toObject = bond.to.objectId === dragged.id ? dragged : other;
      const bondDistance = distanceBetween(
        anchorWorld(graph, fromObject, bond.from),
        anchorWorld(graph, toObject, bond.to),
      );
      const radius = effectiveSnapRadius(Math.max(bond.from.radius, bond.to.radius), profile);
      const blocked =
        isOccupied(graph, bond.from, fromObject, dragged.id) ||
        isOccupied(graph, bond.to, toObject, dragged.id);

      const failure: SnapFailure = {
        reason: blocked ? "occupied" : "too_far",
        bond,
        targetObjectId: other.id,
        targetAnchorLabel: anchorLabel(bond.to, toObject),
        sourceAnchorLabel: anchorLabel(bond.from, fromObject),
        distance: bondDistance,
        radius,
      };

      // 同一对器材里，取最接近成功的那次；被占用不该被「更近但也没成」的挤掉，
      // 所以未被占用的候选只有更近时才顶掉已记录的占用结论。
      if (!best) best = failure;
      else if (best.reason === "occupied" && !blocked) {
        if (bondDistance < (best.distance ?? Infinity)) best = failure;
      } else if (bondDistance < (best.distance ?? Infinity)) best = failure;
    }
  }

  if (best) return best;

  // ③ 这一对之间没有任何语义兼容的锚点 —— 它们本来就不该连
  return {
    reason: sawCompatible ? "no_action" : "incompatible",
    bond: null,
    targetObjectId: other.id,
    targetAnchorLabel: null,
    sourceAnchorLabel: null,
    distance: target.distance,
    radius: null,
  };
}
